import { useAuth0 } from '@auth0/auth0-react';
import {
  useAddress,
  useCreateAddress,
  useDeleteAddress,
  useLocations,
  useOrganisation,
  useUpdateAddress,
} from '@my/directory-api/hooks';
import type {
  Address,
  AddressTypeValue,
  BaseStatusValue,
  PartyTypeValue,
  UpdateAddressRequest,
} from '@my/directory-api';
import { useServiceTags } from '@my/tms-api/hooks';
import type { ServiceTag } from '@my/tms-api';
import { ConfirmDialog, ErrorBanner, Loading, PageHeader, showToast } from '@my/ui';
import {
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  Edit,
  ExternalLink,
  Plus,
  Search,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import { DirectoryHistoryButton } from '@/components/HistoryButtons';
import { AddressEditorPanel } from './AddressEditorPanel';
import {
  EMPTY_ADDRESS_DRAFT,
  addressToDraft,
  applicableLocationTags,
  buildCreateAddressRequest,
  draftTagSet,
  isAddressDraftValid,
  toggleDraftTag,
  type AddressDraft,
} from './locationDraft';

type SortKey = 'name' | 'code' | 'country';
type SortDir = 'asc' | 'desc';
type StatusFilter = BaseStatusValue | 'all';

const STATUS_DOT: Record<BaseStatusValue, string> = {
  Active: 'bg-green-500',
  Inactive: 'bg-slate-400',
};

const STATUS_BADGE: Record<BaseStatusValue, string> = {
  Active: 'bg-green-100 text-green-700',
  Inactive: 'bg-slate-200 text-slate-700',
};

export function LocationListPage() {
  const { user } = useAuth0();
  const orgId = useMemo(() => getCurrentOrganisationId(user), [user]);
  const { t } = useTranslation(['locations', 'common']);
  const navigate = useNavigate();

  const listQuery = useLocations(orgId);
  const serviceTagsQuery = useServiceTags(orgId);
  // Org + main-office address — used purely to seed the default country
  // on a fresh draft so operators creating a new location don't have to
  // re-pick their own country every time. Mirrors the create-mode seed
  // logic on `PartyDetailsPage`. Cheap: both queries are cached and
  // typically already warm by the time the operator hits +Add.
  const orgQuery = useOrganisation(orgId);
  const officeAddressQuery = useAddress(orgQuery.data?.mainOfficeAddressId ?? null);
  const defaultCountryIsoCode = officeAddressQuery.data?.countryIsoCode ?? 'IE';
  const createMutation = useCreateAddress();
  const updateMutation = useUpdateAddress();
  const deleteMutation = useDeleteAddress();

  const isBusy = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  // Service tags applicable to the Locations editor — only Active tags
  // whose `applyToServiceTypes` includes `Locations` (or is null/empty,
  // i.e. "applies everywhere"). Sorted alphabetically so the chip row
  // reads predictably.
  const applicableTags: ServiceTag[] = useMemo(
    () => applicableLocationTags(serviceTagsQuery.data),
    [serviceTagsQuery.data],
  );

  const statusLabels: Record<BaseStatusValue, string> = {
    Active: t('locations:status.active'),
    Inactive: t('locations:status.inactive'),
  };

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<StatusFilter>('all');
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Inline-editor state. `'new'` pins a draft row at the top of the grid;
  // a guid swaps the corresponding row in-place for an editor; `null`
  // means no editor open. `pendingDelete` is held separately so the
  // ConfirmDialog can read the row name even after the editor closes.
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<AddressDraft>(EMPTY_ADDRESS_DRAFT);
  const [pendingDelete, setPendingDelete] = useState<Address | null>(null);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const startNew = () => {
    setEditingId('new');
    // Seed the country from the organisation's main-office address so
    // operators don't have to re-pick `IE` (or whatever their actual
    // country is) on every new location. Falls back to the constant
    // default in `EMPTY_ADDRESS_DRAFT` when the org address is missing.
    setDraft({ ...EMPTY_ADDRESS_DRAFT, countryIsoCode: defaultCountryIsoCode });
  };
  const startEdit = (a: Address) => {
    setEditingId(a.id);
    setDraft(addressToDraft(a));
  };
  const cancel = () => {
    setEditingId(null);
    setDraft(EMPTY_ADDRESS_DRAFT);
  };
  const openParty = (a: Address) => {
    if (!a.partyId) return;
    // Route by party type — Customer / Both → /customers/:id, Supplier → /suppliers/:id.
    // The list page uses the same convention.
    const base = a.partyType === 'Supplier' ? '/suppliers' : '/customers';
    navigate(`${base}/${a.partyId}`);
  };

  // Address is valid for save when name + line1 + 2-letter country are
  // present AND lat/lng are non-null (geolocation verified). Same rule as
  // every other AddressInlineEditor in the suite.
  const isValid = isAddressDraftValid(draft);

  // Lower-cased set of currently-active tag names — used by the chip
  // row to render the on/off state. Same canonicalisation rule the BE
  // applies on save (case-insensitive matching).
  const tagSet = useMemo(() => draftTagSet(draft.serviceTags), [draft.serviceTags]);

  const toggleTag = (tag: ServiceTag) => setDraft(toggleDraftTag(draft, tag));


  const handleSave = async () => {
    if (!isValid || !orgId) return;
    try {
      if (editingId === 'new') {
        const body = buildCreateAddressRequest(orgId, draft);
        await createMutation.mutateAsync(body);
        showToast({ type: 'success', message: t('locations:addresses.created') });
      } else if (typeof editingId === 'string') {
        const existing = (listQuery.data ?? []).find((a) => a.id === editingId);
        const body: UpdateAddressRequest = {
          addressId: editingId,
          // Preserve the row's existing AddressType — operators may have
          // a Task or Location row open and we don't expose the toggle
          // here. Defensive fallback to `Location` for the (unreachable)
          // case where the row vanished mid-edit.
          addressType: existing?.addressType ?? ('Task' as AddressTypeValue),
          status: draft.status,
          code: draft.code.trim() || null,
          name: draft.name.trim(),
          fullAddress: draft.fullAddress.trim(),
          city: draft.city.trim() || null,
          postcode: draft.postcode.trim() || null,
          countryIsoCode: draft.countryIsoCode.toUpperCase(),
          latitude: draft.latitude,
          longitude: draft.longitude,
          coordinatesSource: draft.coordinatesSource ?? null,
          placeId: draft.placeId.trim(),
          serviceTags: draft.serviceTags || null,
        };
        await updateMutation.mutateAsync(body);
        showToast({ type: 'success', message: t('locations:addresses.saved') });
      }
      cancel();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('locations:addresses.saveFailed'),
      });
    }
  };

  const confirmDelete = async () => {
    const target = pendingDelete;
    if (!target) return;
    try {
      // Locations are standalone (no partyId by definition for editable
      // rows here — party-linked rows are read-only on this page), so
      // pass `partyId: null`. The mutation invalidates the org-wide
      // Locations cache via the blanket pattern.
      await deleteMutation.mutateAsync({ id: target.id, partyId: null });
      showToast({ type: 'success', message: t('locations:addresses.deleted') });
      if (editingId === target.id) cancel();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('locations:addresses.deleteFailed'),
      });
    } finally {
      setPendingDelete(null);
    }
  };

  const hasActiveFilters = search.trim() !== '' || filterStatus !== 'all';
  const clearFilters = () => {
    setSearch('');
    setFilterStatus('all');
  };

  // ── Derived data ─────────────────────────────────────────────────────────

  const visibleRows = useMemo(() => {
    const all = listQuery.data ?? [];
    const q = search.trim().toLowerCase();
    const filtered = all.filter((a) => {
      // Primary rows: include customers' main addresses in the dispatch
      // pool, exclude Supplier-only primaries (pickup-via-PO, not
      // standalone stops). Filter is "exclude Supplier" rather than
      // "include Customer / Both" so the row still shows even if the
      // BE response happens to be missing `partyType` for some reason
      // (stale schema, missing Include, etc.) — defensive default is
      // "show, don't hide". The BE returns every Primary row in the
      // org; we narrow here.
      if (a.addressType === 'Primary' && a.partyType === 'Supplier') {
        return false;
      }
      if (q) {
        // Search across the visible-column fields. The party column matters
        // — operators look up "every stop tied to ACME" by typing the party
        // name. Country and service-tag list join in for the same reason.
        const haystack = [
          a.name,
          a.code ?? '',
          a.fullAddress,
          a.city ?? '',
          a.postcode ?? '',
          a.countryIsoCode,
          a.serviceTags ?? '',
          a.partyName ?? '',
        ]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (filterStatus !== 'all' && a.status !== filterStatus) return false;
      return true;
    });
    return applySort(filtered, sortKey, sortDir);
  }, [listQuery.data, search, filterStatus, sortKey, sortDir]);

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="flex h-full flex-col px-2">
      <PageHeader title={t('locations:title')} description={t('locations:description')}>
        {/* Locations only. An address belonging to a customer or a site files
            under that record instead, so this stays about the address book
            rather than quietly showing every party's addresses too. */}
        <DirectoryHistoryButton
          organisationId={orgId}
          aggregateType="Address"
          wholeType
          subject={t('locations:title')}
          variant="subtle"
        />
      </PageHeader>

      <main className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto pb-4">
        {!orgId && (
          <ErrorBanner
            title="No organisation linked to this account"
            message="Your access token does not include a TMS organisation claim. Contact an administrator."
          />
        )}

        {orgId && listQuery.isPending && <Loading label={t('locations:title')} />}

        {orgId && listQuery.isError && (
          <ErrorBanner title={t('locations:loadFailed')} message={listQuery.error.message} />
        )}

        {orgId && listQuery.isSuccess && (
          <>
            {/* ── Filters + search + add button ─────────────────────────── */}
            <div className="flex flex-wrap items-center gap-2">
              {(['all', 'Active', 'Inactive'] as StatusFilter[]).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setFilterStatus(s)}
                  className={`inline-flex items-center gap-1 ${chipClass(filterStatus === s)}`}
                >
                  {s !== 'all' && (
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${STATUS_DOT[s]}`}
                      aria-hidden="true"
                    />
                  )}
                  {s === 'all'
                    ? t('locations:filters.allStatuses')
                    : s === 'Active'
                      ? t('locations:filters.active')
                      : t('locations:filters.inactive')}
                </button>
              ))}

              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="text-[10px] text-slate-400 underline hover:text-slate-600"
                >
                  {t('locations:filters.clear')}
                </button>
              )}

              <div className="flex-1" />

              {/* Search */}
              <div className="relative">
                <Search
                  className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
                  aria-hidden="true"
                />
                <input
                  type="text"
                  placeholder={t('locations:search')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-52 rounded-full border border-slate-300 bg-white py-1.5 pl-8 pr-3 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>

              <button
                type="button"
                onClick={startNew}
                disabled={isBusy || editingId !== null}
                className="flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-purple-600 hover:bg-purple-50 disabled:opacity-50"
              >
                <Plus className="h-3.5 w-3.5" />
                {t('common:actions.add')}
              </button>
            </div>

            {/* ── Table ─────────────────────────────────────────────────── */}
            {/* `rounded-md` lives on the scroll container so the rounded
                corners are preserved; the inner div used to carry
                `overflow-hidden rounded-md` together but the
                `overflow-hidden` was clipping any table content that
                grew wider than `min-w-[768px]` (the editor row's
                AddressInlineEditor + map split-pane is the typical
                culprit). With it clipped, the outer
                `overflow-x-auto` saw nothing past 768px and the user
                couldn't scroll to reach it on mobile. */}
            <div className="overflow-x-auto rounded-md">
              <div className="min-w-[768px]">
                <table className="w-full table-auto text-xs">
                  <thead className="bg-slate-700 text-[11px] uppercase tracking-wide text-white">
                    <tr>
                      <SortableTh
                        label={t('locations:table.name')}
                        sortKey="name"
                        active={sortKey}
                        dir={sortDir}
                        onSort={handleSort}
                      />
                      <SortableTh
                        label={t('locations:table.code')}
                        sortKey="code"
                        active={sortKey}
                        dir={sortDir}
                        onSort={handleSort}
                      />
                      <Th>{t('locations:table.address')}</Th>
                      <Th>{t('locations:table.city')}</Th>
                      <Th>{t('locations:table.postcode')}</Th>
                      <SortableTh
                        label={t('locations:table.country')}
                        sortKey="country"
                        active={sortKey}
                        dir={sortDir}
                        onSort={handleSort}
                        align="center"
                      />
                      <Th>{t('locations:table.party')}</Th>
                      <Th>{t('locations:table.serviceTags')}</Th>
                      <Th align="center">{t('locations:table.status')}</Th>
                      {/* Actions column is pinned to the right of the
                          horizontal scroll viewport so the Edit pencil
                          / Open-party icon stays reachable on mobile
                          (the rest of the table is wider than the
                          phone viewport). The sticky cell carries its
                          own slate-700 background so scrolling row
                          content doesn't bleed through. */}
                      <th className="sticky right-0 z-10 whitespace-nowrap bg-slate-700 px-3 py-2 text-right font-semibold">
                        {t('locations:table.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-500 bg-white">
                    {/* New-row draft pinned to the top of the grid */}
                    {editingId === 'new' && (
                      <tr>
                        <td colSpan={10} className="bg-slate-50 px-3 py-3">
                          <AddressEditorPanel
                            draft={draft}
                            onChange={setDraft}
                            onSave={handleSave}
                            onCancel={cancel}
                            isBusy={isBusy}
                            isValid={isValid}
                            applicableTags={applicableTags}
                            tagSet={tagSet}
                            onToggleTag={toggleTag}
                          />
                        </td>
                      </tr>
                    )}

                    {visibleRows.length === 0 && editingId !== 'new' ? (
                      <tr>
                        <td
                          colSpan={10}
                          className="px-3 py-6 text-center text-xs italic text-slate-400"
                        >
                          {hasActiveFilters
                            ? t('locations:table.emptyFiltered')
                            : t('locations:table.empty', { add: t('common:actions.add') })}
                        </td>
                      </tr>
                    ) : (
                      visibleRows.map((a) =>
                        editingId === a.id ? (
                          // Same-line edit — the row swaps for an editor
                          // row spanning every column. Visually replaces
                          // the data row in place so the operator stays
                          // anchored to the record they were on.
                          <tr key={a.id}>
                            <td colSpan={10} className="bg-slate-50 px-3 py-3">
                              {/* Read-only preview when the row is owned
                                  elsewhere:
                                    - `partyId != null` → managed on the
                                      Party Details page; we surface an
                                      "Open party" external-link action.
                                    - `addressType === 'Primary'` with no
                                      party → the organisation's main-
                                      office address (managed on the
                                      Organisation Settings page); the
                                      operator closes the preview and
                                      navigates manually.
                                  Editable only when neither applies — i.e.
                                  standalone Task / Location rows the
                                  operator created from this page. */}
                              {(() => {
                                const isPartyLinked = a.partyId != null;
                                const isOrgPrimary =
                                  a.addressType === 'Primary' && !isPartyLinked;
                                const isReadOnly = isPartyLinked || isOrgPrimary;
                                // Per-source hint shown at the bottom of the
                                // editor: explains where the operator should
                                // actually go to update this address.
                                // Distinguishes customer / supplier party
                                // links from organisation-primary rows so
                                // the wording is precise.
                                const readOnlyHint = isPartyLinked
                                  ? t('locations:readOnlyHint.party', {
                                      name: a.partyName ?? '',
                                      type:
                                        a.partyType === 'Supplier'
                                          ? t('locations:partyType.supplier')
                                          : t('locations:partyType.customer'),
                                    })
                                  : isOrgPrimary
                                    ? t('locations:readOnlyHint.organisation')
                                    : undefined;
                                return (
                                  <AddressEditorPanel
                                    draft={draft}
                                    onChange={setDraft}
                                    onSave={handleSave}
                                    onCancel={cancel}
                                    onDelete={
                                      isReadOnly ? undefined : () => setPendingDelete(a)
                                    }
                                    isBusy={isBusy}
                                    isValid={isValid}
                                    applicableTags={applicableTags}
                                    tagSet={tagSet}
                                    onToggleTag={toggleTag}
                                    readOnly={isReadOnly}
                                    onOpenParty={
                                      isPartyLinked ? () => openParty(a) : undefined
                                    }
                                    readOnlyHint={readOnlyHint}
                                  />
                                );
                              })()}
                            </td>
                          </tr>
                        ) : (
                          <LocationRow
                            key={a.id}
                            address={a}
                            statusLabel={statusLabels[a.status]}
                            // Lock the pencil while another row is open
                            // for edit / a draft is in progress so the
                            // operator can't half-fill two records at once.
                            rowsLocked={editingId !== null || isBusy}
                            onEdit={() => startEdit(a)}
                          />
                        ),
                      )
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>

      <ConfirmDialog
        open={pendingDelete !== null}
        tone="danger"
        title={t('locations:addresses.delete.title')}
        message={
          pendingDelete
            ? t('locations:addresses.delete.message', {
                name: pendingDelete.name || pendingDelete.fullAddress,
              })
            : null
        }
        confirmLabel={t('common:actions.delete')}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
        busy={deleteMutation.isPending}
      />
    </div>
  );
}

// ── Components ──────────────────────────────────────────────────────────────

function Th({ children, align }: { children: React.ReactNode; align?: 'right' | 'center' }) {
  const alignClass =
    align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return <th className={`whitespace-nowrap px-3 py-2 font-semibold ${alignClass}`}>{children}</th>;
}

function SortableTh({
  label,
  sortKey,
  active,
  dir,
  onSort,
  align,
}: {
  label: string;
  sortKey: SortKey;
  active: SortKey | null;
  dir: SortDir;
  onSort: (k: SortKey) => void;
  align?: 'right' | 'center';
}) {
  const isActive = active === sortKey;
  const Icon = isActive ? (dir === 'asc' ? ChevronUp : ChevronDown) : ChevronsUpDown;
  const alignClass =
    align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return (
    <th
      className={`group cursor-pointer select-none whitespace-nowrap px-3 py-2 font-semibold ${alignClass}`}
      onClick={() => onSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <Icon
          className={`h-3 w-3 flex-shrink-0 transition-opacity ${
            isActive ? 'opacity-100' : 'opacity-40 group-hover:opacity-70'
          }`}
          aria-hidden="true"
        />
      </span>
    </th>
  );
}

interface LocationRowProps {
  address: Address;
  statusLabel: string;
  /** Another row is being edited / a draft is open — disable the pencil. */
  rowsLocked: boolean;
  onEdit: () => void;
}

function LocationRow({ address, statusLabel, rowsLocked, onEdit }: LocationRowProps) {
  const { t } = useTranslation('locations');

  // Address column shows the Google-formatted full line. City +
  // Postcode get their own dedicated columns alongside, mirroring the
  // legacy grid layout — the full line provides extra context (unit,
  // sublocality, county) the structured fields don't carry.
  const summary = address.fullAddress || null;

  // Country cell — bare ISO code, matching the parties grid. Flag
  // emoji are skipped here because Windows renders the regional-
  // indicator characters as lowercase letters (`🇮🇪` → "ie") which,
  // combined with the ISO suffix, reads as duplicated ("ie IE").

  // Service-tag chip row — comma-joined string from the BE; split + trim
  // + drop blanks. Clamped to two visible chips with a "+N" overflow so
  // the column stays narrow on long lists.
  const tags = (address.serviceTags ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const visibleTags = tags.slice(0, 2);
  const extraTags = tags.length - visibleTags.length;

  // Read-only when the row is owned elsewhere — either linked to a
  // party (managed on the Party Details page) or it's an org-level
  // Primary record (the organisation's main-office address, managed
  // on Organisation Settings). For both cases the action button on
  // the row swaps to an ExternalLink icon and the editor opens in
  // preview mode.
  const isPartyLinked = address.partyId != null;
  const isOrgPrimary = address.addressType === 'Primary' && !isPartyLinked;
  const isReadOnly = isPartyLinked || isOrgPrimary;

  // Double-click opens the editor for both standalone and party-linked
  // rows. Standalone rows get the full edit experience; party-linked
  // rows open the editor in read-only preview (fields + map) with an
  // "Open party" action inside. Locked rows (another row mid-edit)
  // ignore the click so the operator can't half-fill two records.
  const handleDoubleClick = () => {
    if (!rowsLocked) onEdit();
  };

  return (
    // `group` lets the sticky Actions cell inherit the row's hover
    // state via `group-hover:bg-slate-50` — without it the sticky
    // cell would keep its solid white background through hover and
    // look visually disconnected from the rest of the row.
    <tr
      className={`group cursor-pointer hover:bg-slate-50 ${rowsLocked && !isReadOnly ? 'opacity-50' : ''}`}
      onDoubleClick={handleDoubleClick}
    >
      <td className="whitespace-nowrap px-3 py-1 align-top text-xs font-semibold text-slate-900">
        {address.name || address.fullAddress}
      </td>
      <td className="whitespace-nowrap px-3 py-1 align-top text-xs font-semibold text-slate-700">
        {address.code || <span className="font-normal italic text-slate-300">—</span>}
      </td>
      <td className="px-3 py-1 align-top text-xs text-slate-500">
        {summary ?? <span className="italic text-slate-300">—</span>}
      </td>
      <td className="whitespace-nowrap px-3 py-1 align-top text-xs text-slate-500">
        {address.city || <span className="italic text-slate-300">—</span>}
      </td>
      <td className="whitespace-nowrap px-3 py-1 align-top text-xs text-slate-500">
        {address.postcode || <span className="italic text-slate-300">—</span>}
      </td>
      <td className="px-3 py-1 text-center align-top text-xs text-slate-500">
        {address.countryIsoCode}
      </td>
      <td className="px-3 py-1 align-top text-xs">
        {isPartyLinked ? (
          <PartyLinkLabel partyName={address.partyName ?? ''} partyType={address.partyType} />
        ) : (
          <span className="italic text-slate-300">—</span>
        )}
      </td>
      <td className="px-3 py-1 align-top text-xs">
        {visibleTags.length === 0 ? (
          <span className="italic text-slate-300">—</span>
        ) : (
          <span className="inline-flex flex-wrap gap-1">
            {visibleTags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700"
              >
                {tag}
              </span>
            ))}
            {extraTags > 0 && (
              <span className="inline-flex items-center rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">
                +{extraTags}
              </span>
            )}
          </span>
        )}
      </td>
      <td className="px-3 py-1 text-center align-top">
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${STATUS_BADGE[address.status]}`}
        >
          {statusLabel}
        </span>
      </td>
      {/* Sticky-right pin: see the matching `<th>` comment above for
          why. `bg-white` + `group-hover:bg-slate-50` keeps the cell
          opaque (so scrolling row content can't bleed through) while
          still picking up the row hover state. */}
      <td className="sticky right-0 z-10 whitespace-nowrap bg-white px-3 py-1 text-right align-top group-hover:bg-slate-50">
        {isReadOnly ? (
          // Read-only rows (party-linked or org-level Primary) open the
          // editor in preview mode — full address + map, no Save /
          // Delete. The editor exposes an "Open party" external link
          // for party-linked rows; org-Primary rows are managed on the
          // Organisation Settings page (operator closes + navigates).
          // The row icon is ExternalLink for both as a visual cue that
          // the source lives elsewhere.
          <button
            type="button"
            aria-label={
              isPartyLinked
                ? t('rowActions.openParty', { name: address.partyName ?? '' })
                : t('rowActions.openOrgPrimary')
            }
            title={
              isPartyLinked
                ? t('rowActions.openPartyTitle')
                : t('rowActions.openOrgPrimaryTitle')
            }
            disabled={rowsLocked}
            onClick={(e) => {
              e.stopPropagation();
              if (!rowsLocked) onEdit();
            }}
            className="inline-flex h-5 w-5 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-100 disabled:opacity-50"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </button>
        ) : (
          <button
            type="button"
            aria-label={`Edit ${address.name || address.fullAddress}`}
            disabled={rowsLocked}
            onClick={(e) => {
              e.stopPropagation();
              if (!rowsLocked) onEdit();
            }}
            className="inline-flex h-5 w-5 items-center justify-center rounded-sm text-blue-600 hover:bg-blue-50 disabled:opacity-50"
          >
            <Edit className="h-3.5 w-3.5" />
          </button>
        )}
      </td>
    </tr>
  );
}

/**
 * "Linked to {party}" cell. Renders the party name plus a tiny
 * Customer / Supplier badge (mirrors the `PartyType` enum on the BE)
 * so operators can tell at a glance whether the linked record is a
 * customer or supplier without opening it.
 */
function PartyLinkLabel({
  partyName,
  partyType,
}: {
  partyName: string;
  partyType: PartyTypeValue | null | undefined;
}) {
  const { t } = useTranslation('locations');
  // Party type only present when the BE-side join populated it. If
  // missing we still render the name without a badge — degrading
  // gracefully rather than dropping the link.
  const typeLabel =
    partyType === 'Customer'
      ? t('partyType.customer')
      : partyType === 'Supplier'
        ? t('partyType.supplier')
        : partyType === 'Both'
          ? t('partyType.both')
          : null;
  return (
    <span className="inline-flex items-center gap-1">
      <span className="truncate text-slate-700">{partyName}</span>
      {typeLabel && (
        <span className="inline-flex items-center rounded-full bg-purple-50 px-1.5 py-0.5 text-[10px] font-medium text-purple-700">
          {typeLabel}
        </span>
      )}
    </span>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function applySort(list: Address[], key: SortKey | null, dir: SortDir): Address[] {
  if (!key) {
    // Default: name ascending. Matches what operators expect on first paint.
    return [...list].sort((a, b) =>
      (a.name || a.fullAddress).localeCompare(b.name || b.fullAddress, undefined, {
        sensitivity: 'base',
      }),
    );
  }
  const mul = dir === 'asc' ? 1 : -1;
  return [...list].sort((a, b) => {
    switch (key) {
      case 'name':
        return (
          mul *
          (a.name || a.fullAddress).localeCompare(b.name || b.fullAddress, undefined, {
            sensitivity: 'base',
          })
        );
      case 'code':
        // Empty codes sort last regardless of direction — a list sorted
        // by code is most useful when the populated rows cluster.
        if (!a.code && !b.code) return 0;
        if (!a.code) return 1;
        if (!b.code) return -1;
        return mul * a.code.localeCompare(b.code, undefined, { sensitivity: 'base' });
      case 'country':
        return (
          mul * a.countryIsoCode.localeCompare(b.countryIsoCode, undefined, { sensitivity: 'base' })
        );
    }
  });
}

function chipClass(active: boolean): string {
  return `rounded-full border px-2.5 py-0.5 text-[10px] font-medium transition-colors ${
    active
      ? 'border-slate-700 bg-slate-700 text-white'
      : 'border-slate-300 bg-white text-slate-500 hover:border-slate-500 hover:text-slate-700'
  }`;
}
